import React, { forwardRef, useState, useMemo } from "react";
import styles from "./styles/InputNumber.module.css";
import { capitalizeFirstLetter } from "../../../utils/utils";

/**
 * Input text component
 * ForwardRef is used to focus on the element when mounted since it is a component
 */
const InputNumber = forwardRef(
    (
        {
            inputId,
            inputValue,
            handleChangeFunction,
            required,
            placeholder,
            letterSpacing,
            textAlign,
            maxLength,
            data,
            disable,
            adaptFont,
            maxValue,
            minValue,
            step,
        },
        ref
    ) => {
        const locale = navigator.language || "en-US";

        // Get the decimal separator for the current locale
        const decimalSeparator = useMemo(() => {
            const numberFormat = new Intl.NumberFormat(locale);
            const parts = numberFormat.formatToParts(1.1);
            return parts.find((part) => part.type === "decimal")?.value || ".";
        }, [locale]);

        // Convert display value (with locale separator) to standard format (with dot)
        const convertToStandardFormat = (displayValue) => {
            if (decimalSeparator === ".") return displayValue;
            return displayValue.replace(decimalSeparator, ".");
        };

        // Convert standard format (with dot) to display format (with locale separator)
        const convertToDisplayFormat = (standardValue) => {
            if (decimalSeparator === "." || !standardValue)
                return standardValue;
            return standardValue.replace(".", decimalSeparator);
        };

        const filterOutPut = (event) => {
            let displayInput = event.target.value;

            // Convert display input to standard format for processing
            let standardInput = convertToStandardFormat(displayInput);

            // Allow empty input
            if (standardInput === "") {
                handleChangeFunction({
                    target: { name: inputId, value: "" },
                });
                return;
            }

            // Allow only digits and one dot (but allow it at the start for .01)
            if (!/^\d*\.?\d*$/.test(standardInput)) return;

            // Prevent numbers with leading zeros (e.g., 0011 is not allowed)
            if (/^0\d/.test(standardInput)) {
                return;
            }

            // Special case for allowing inputs like ".01" and ensuring it becomes "0.01"
            if (standardInput.startsWith(".")) {
                standardInput = "0" + standardInput; // Convert ".01" to "0.01"
            }

            // Prevent more than 2 decimals
            const parts = standardInput.split(".");
            if (parts[1]?.length > 2) return;

            // Only check max if it's a valid number
            const num = parseFloat(standardInput);

            if (!isNaN(num)) {
                if (maxValue !== undefined && num > maxValue) {
                    return;
                }
            }

            // Store the standard format but let the display show locale format
            handleChangeFunction({
                target: { name: inputId, value: standardInput },
            });
        };

        // Handle key input to allow typing the locale decimal separator
        const handleKeyDown = (event) => {
            // If user types the locale decimal separator, allow it
            if (event.key === decimalSeparator && decimalSeparator !== ".") {
                const currentValue = event.target.value;
                const standardValue = convertToStandardFormat(currentValue);

                // Check if dot is already present in standard format
                if (standardValue.includes(".")) {
                    event.preventDefault();
                    return;
                }

                // Allow the locale separator to be typed
                event.preventDefault();
                const newDisplayValue = currentValue + decimalSeparator;
                const newStandardValue =
                    convertToStandardFormat(newDisplayValue);

                handleChangeFunction({
                    target: { name: inputId, value: newStandardValue },
                });
            }
        };

        // Get the display value (convert from standard format to locale format)
        const displayValue = convertToDisplayFormat(inputValue || "");

        return (
            <div className={`${styles.input_container} v-flex-container`}>
                <input
                    ref={ref}
                    data-ex={data && data}
                    className={`${styles.input_element} ${
                        required && styles.alert
                    } ${disable ? styles.disabled : ""}  ${
                        adaptFont ? styles.adaptFont : ""
                    }`}
                    style={{
                        letterSpacing: letterSpacing,
                        textAlign: textAlign,
                    }}
                    type={"text"}
                    name={inputId}
                    id={inputId}
                    value={displayValue}
                    placeholder={
                        required ? capitalizeFirstLetter(placeholder) : ""
                    }
                    onChange={filterOutPut}
                    onKeyDown={handleKeyDown}
                    maxLength={maxLength}
                    spellCheck={false}
                    autoComplete={"off"}
                    max={maxValue || undefined}
                    min={minValue || undefined}
                    step={step}
                    disabled={disable}
                />
            </div>
        );
    }
);

export default InputNumber;
